import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X } from 'lucide-react';

const STATUS_CONFIG = {
  'quote': { label: 'Quote', color: '#94a3b8', bgColor: '#f1f5f9' },
  'accepted': { label: 'Accepted', color: '#3b82f6', bgColor: '#dbeafe' },
  'deposit-taken': { label: 'Deposit Taken', color: '#10b981', bgColor: '#d1fae5' },
  'artwork-created': { label: 'Artwork Created', color: '#8b5cf6', bgColor: '#ede9fe' },
  'artwork-approved': { label: 'Artwork Approved', color: '#059669', bgColor: '#d1fae5' },
  'changes-requested': { label: 'Changes Requested', color: '#f59e0b', bgColor: '#fef3c7' },
  'application-sent': { label: 'Application Sent', color: '#0891b2', bgColor: '#cffafe' },
  'application-fee-paid': { label: 'Application Fee Paid', color: '#06b6d4', bgColor: '#cffafe' },
  'application-approved': { label: 'Application Approved', color: '#10b981', bgColor: '#d1fae5' },
  'stone-ordered': { label: 'Stone Ordered', color: '#6366f1', bgColor: '#e0e7ff' },
  'stone-in-stock': { label: 'Stone in Stock', color: '#8b5cf6', bgColor: '#ede9fe' },
  'work-started': { label: 'Work Started', color: '#f59e0b', bgColor: '#fef3c7' },
  'memorial-completed': { label: 'Memorial Completed', color: '#10b981', bgColor: '#d1fae5' },
  // Legacy statuses
  'pending': { label: 'Pending', color: '#94a3b8', bgColor: '#f1f5f9' },
  'in-progress': { label: 'In Progress', color: '#f59e0b', bgColor: '#fef3c7' },
  'completed': { label: 'Completed', color: '#10b981', bgColor: '#d1fae5' }
};

function StatusChart({ orders, selectedStatus, onStatusClick }) {
  // Count orders by status
  const statusCounts = orders.reduce((acc, order) => {
    const status = order.status || 'pending';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // Virtual "needs artwork attention" bucket — accepted/deposit-taken orders
  // still awaiting initial artwork PLUS any order the client sent back with
  // changes requested. Gives the shop a single click-to-see-all view of
  // "what needs the designer's attention right now".
  const NEEDS_ARTWORK_STATUSES = ['accepted', 'deposit-taken', 'changes-requested'];
  // Restorations, maintenance and grave vaults never need artwork — exclude them
  const noArtworkType = (o) => ['restoration', 'maintenance', 'grave vault'].includes(
    (o.order_type || '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim());
  const needsArtworkCount = orders.filter(o => NEEDS_ARTWORK_STATUSES.includes(o.status) && !noArtworkType(o)).length;

  // Get total orders
  const totalOrders = orders.length;

  // Sort statuses by count (descending)
  const sortedStatuses = Object.entries(statusCounts)
    .sort(([, a], [, b]) => b - a)
    .filter(([status]) => STATUS_CONFIG[status]); // Only show configured statuses

  const needsArtworkSelected = selectedStatus === 'needs-artwork';

  return (
    <Card className="border-0 shadow-lg mb-6">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span>Order Status Overview</span>
            {selectedStatus && (
              <button
                onClick={() => onStatusClick(null)}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-200 hover:bg-slate-300 rounded-full transition-colors"
                data-testid="clear-status-filter-btn"
              >
                <X className="w-3 h-3" />
                Clear filter
              </button>
            )}
          </div>
          <span className="text-sm font-normal text-slate-500">{totalOrders} Total Orders</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {/* Virtual "Needs Artwork" filter card — always shown so the user
              can click even if the count is 0 today. */}
          <div
            onClick={() => onStatusClick(needsArtworkSelected ? null : 'needs-artwork')}
            className={`p-3 rounded-lg transition-all cursor-pointer border-2 border-dashed ${
              needsArtworkSelected
                ? 'scale-105 shadow-lg border-amber-500 bg-amber-100'
                : 'border-amber-400 bg-amber-50 hover:scale-105 hover:shadow-md'
            }`}
            style={{ borderStyle: 'dashed' }}
            data-testid="status-filter-needs-artwork"
          >
            <div className="flex flex-col">
              <div className="flex items-center justify-between mb-1">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-xl font-bold text-amber-700">{needsArtworkCount}</span>
              </div>
              <p className="text-xs font-medium text-amber-900 leading-tight">
                Needs Artwork / Changes
              </p>
              <p className="text-xs text-amber-700 mt-1">Accepted • Deposit • Changes</p>
            </div>
          </div>

          {sortedStatuses.map(([status, count]) => {
            const config = STATUS_CONFIG[status];
            const percentage = ((count / totalOrders) * 100).toFixed(0);
            const isSelected = selectedStatus === status;
            
            return (
              <div
                key={status}
                onClick={() => onStatusClick(status)}
                className={`p-3 rounded-lg transition-all cursor-pointer ${
                  isSelected 
                    ? 'scale-105 shadow-lg ring-2 ring-offset-2' 
                    : 'hover:scale-105 hover:shadow-md'
                }`}
                style={{ 
                  backgroundColor: config.bgColor,
                  ringColor: isSelected ? config.color : 'transparent'
                }}
              >
                <div className="flex flex-col">
                  <div className="flex items-center justify-between mb-1">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: config.color }}
                    />
                    <span className="text-xl font-bold" style={{ color: config.color }}>
                      {count}
                    </span>
                  </div>
                  <p className="text-xs font-medium text-slate-700 leading-tight">
                    {config.label}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{percentage}%</p>
                </div>
              </div>
            );
          })}
        </div>
        
        {sortedStatuses.length === 0 && (
          <p className="text-center text-slate-500 py-8">No orders to display</p>
        )}
      </CardContent>
    </Card>
  );
}

export default StatusChart;
export { STATUS_CONFIG };